import * as sentry from "@sentry/react";
import trackCustomerIOAnalytics from "./handlers/trackCustomerIOAnalytics";
import trackFBAnalytics from "./handlers/trackFBAnalytics";
import trackFPRAnalytics from "./handlers/trackFPRAnalytics";
import trackGTagAnalytics from "./handlers/trackGTagAnalytics";
import trackPosthogAnalytics from "./handlers/trackPosthogAnalytics";
import trackRedditAnalytics from "./handlers/trackRedditAnalytics";
import tiktokAnalytic from "./handlers/trackTiktokAnalytics";
import trackTwitterAnalytics from "./handlers/trackTwitterAnalytics";

import { IS_DEV } from "@/data/constants";
import { TRACKING_TYPE, TSignUpType, TTrackingType } from "./event-types";

type TProperties = Record<string, any> & {
  signuptype?: TSignUpType;
};

interface TrackOptions {
  types?: TTrackingType[];
  properties?: TProperties;
  skipPropertiesFor?: TTrackingType[];
}

// Mapping of tracking handlers
const trackingHandlers: Record<
  TTrackingType,
  (event: string, properties: Record<string, any>) => void
> = {
  gtag: trackGTagAnalytics,
  reddit: trackRedditAnalytics,
  facebook: trackFBAnalytics,
  twitter: trackTwitterAnalytics,
  fpr: trackFPRAnalytics,
  posthog: trackPosthogAnalytics,
  tiktok: tiktokAnalytic,
  customerio: trackCustomerIOAnalytics,
};

// Allow both string and object event names
type EventName = string | Partial<Record<TTrackingType, string>>;

/**
 * Tracks an event across multiple analytics integrations (Google, Reddit, Facebook, Twitter, etc.).
 *
 * ## Purpose
 * This helper provides a single, unified way to send events to multiple analytics providers
 * without duplicating logic across the codebase. It supports:
 * - Sending the same event name to all providers.
 * - Sending different event names per provider.
 * - Passing event properties (metadata).
 * - Skipping properties for specific providers.
 *
 * ## Parameters
 * @param event - The event name(s) to send.
 *   - If a `string`, that name is used for all tracking types.
 *   - If an object (`Partial<Record<TTrackingType, string>>`), the keys are tracking types
 *     and the values are type-specific event names.
 * @param options - Optional configuration object:
 *   - `types` — An array of tracking types to send the event to.
 *     Defaults to **all** keys in `trackingHandlers`.
 *   - `properties` — Metadata to send with the event. Can include `signuptype` and any custom fields.
 *   - `skipPropertiesFor` — Array of tracking types for which properties should **not** be sent
 *     (empty object `{}` is passed instead).
 *
 * ## Notes
 * - This function will **not** send events when `IS_DEV` is `true` (development mode),
 *   but will log them to the console with `[Tracking Disabled]`.
 * - All tracking errors are caught, logged to console, and sent to Sentry via `sentry.captureException`.
 * - If a tracking handler is missing for a given type, it is silently skipped.
 *
 * ## Example: Send one event name to all providers
 * ```ts
 * eventTrackingHandler("signup_completed", {
 *   properties: { plan: "pro", signuptype: "email" },
 * });
 * ```
 *
 * ## Example: Send different event names to different providers
 * ```ts
 * eventTrackingHandler(
 *   { gtag: "signup_google", facebook: "signup_fb" },
 *   { properties: { plan: "free" } }
 * );
 * ```
 *
 * ## Example: Skip properties for certain providers
 * ```ts
 * eventTrackingHandler("page_view", {
 *   skipPropertiesFor: ["gtag", "reddit"],
 *   properties: { referrer: "homepage" },
 * });
 * ```
 *
 * ## Example: Send only to specific providers
 * ```ts
 * eventTrackingHandler("purchase_made", {
 *   types: ["gtag", "facebook"],
 *   properties: { amount: 29.99, currency: "USD" },
 * });
 * ```
 */

const eventTrackingHandler = (event: EventName, options?: TrackOptions) => {
  if (IS_DEV) {
    console.log(
      `%c[Tracking Disabled] Event: ${JSON.stringify(event)}`,
      "color: yellow; font-weight: bold;",
    );
    return;
  }

  const {
    types = Object.keys(trackingHandlers) as TTrackingType[], // Default to all tracking types
    properties = {},
    skipPropertiesFor = [],
  } = options || {};

  try {
    types.forEach((type) => {
      const trackEvent = trackingHandlers[type];

      if (!trackEvent) return;

      if (typeof event === "string") {
        // Single event string: use for all types
        trackEvent(event, skipPropertiesFor.includes(type) ? {} : properties);
      } else {
        // Object event mapping: use type-specific event name. Customer.io falls
        // back to the posthog name so object-form calls are not silently skipped.
        const eventName =
          event[type] ??
          (type === TRACKING_TYPE.CUSTOMERIO
            ? event[TRACKING_TYPE.POSTHOG]
            : undefined);
        if (eventName) {
          trackEvent(
            eventName,
            skipPropertiesFor.includes(type) ? {} : properties,
          );
        }
      }
    });
  } catch (e) {
    console.error(`Tracking failed for event: ${JSON.stringify(event)}`, e);
    sentry.captureException(e);
  }
};

export default eventTrackingHandler;
